import React, { useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { apiService } from '../../../../services/api';
import { useToast } from '../../../../contexts/ToastContext';
import { useLanguage } from '../../../../contexts/LanguageContext';
import { queryKeys } from '../../../../lib/queryKeys';
import { resolveImageUrl } from '../../../../utils/imageUrl';
import { normalizeApiError } from '../../../../types/api-errors';
import { BusinessHoursMatrix } from '../../../../components/BusinessHoursMatrix';
// AB#1842 (PBI-F) — Blocker dialog + Super Admin override-request modal.
// Opens when the owner attempts to flip ServiceType from Everyone to a
// restrictive value while mismatched staff still exist.
import {
    StaffMismatchBlockingDialog,
    SuperAdminOverrideRequestModal,
    type MismatchedStaff,
} from '../../../../components/StaffGenderMismatchBlocker';
import { ServiceType, Gender } from '../../../../types/enums';
import type { BusinessStaffDto } from '../../../../types/api-responses';
import {
    businessDetailsFormSchema,
    defaultBusinessDetailsForm,
    type BusinessDetailsForm,
    type BusinessDetailsFormInput,
    type HolidayClosureForm,
    type OtherSettingsForm,
} from '../../../../schemas/business.schema';
import {
    useBusinessDetailsQuery,
    useUpdateBusinessMutation,
    useBusinessTypesQuery,
    useCreateHolidayClosureMutation,
    useUpdateHolidayClosureMutation,
    useDeleteHolidayClosureMutation,
    mapResponseToForm,
    mapFormToUpdateRequest,
    loadLocalDraft,
    saveLocalDraft,
    useDebouncedSaveLocalDraft,
} from '../../../../hooks/useBusinessDetails';
import { useAuthContext } from '../../../../contexts/AuthContext';
import { deriveBusinessLoadState } from './businessLoadState';
import DateField from '../../../../components/DateField/DateField';
import { interpolate } from '../../../../components/BusinessHoursMatrix/interpolate';
import { translateValidationMessage } from '../../../../lib/ownerValidationMessages';
import styles from './Section.module.css';

/**
 * AB#1675 Phase A — Business Details sub-section.
 *
 * Wired to the live BE for the fields the API supports today (Name, About,
 * ServiceType, Country, BusinessHours per day) via TanStack Query +
 * react-hook-form + Zod. Phase-B-deferred fields (Holiday Closures, Social
 * Links, multi-Business-Type, Decimal Value, three booking toggles, Arabic
 * fields, Email/Phone/Website, Logo) keep their UI but persist to
 * localStorage with a subtle hint near the Save action — they'll switch to
 * a real PUT shape under the same form schema when Phase B BE work lands.
 */

const COUNTRIES: { code: string; flag: string; nameEn: string; nameAr: string; currency: string; lang: string }[] = [
    { code: 'AE', flag: '🇦🇪', nameEn: 'United Arab Emirates', nameAr: 'الإمارات', currency: 'AED', lang: 'ar' },
    { code: 'JO', flag: '🇯🇴', nameEn: 'Jordan',                nameAr: 'الأردن',  currency: 'JOD', lang: 'ar' },
    { code: 'SA', flag: '🇸🇦', nameEn: 'Saudi Arabia',          nameAr: 'السعودية', currency: 'SAR', lang: 'ar' },
    { code: 'EG', flag: '🇪🇬', nameEn: 'Egypt',                 nameAr: 'مصر',      currency: 'EGP', lang: 'ar' },
    { code: 'KW', flag: '🇰🇼', nameEn: 'Kuwait',                nameAr: 'الكويت',   currency: 'KWD', lang: 'ar' },
    { code: 'BH', flag: '🇧🇭', nameEn: 'Bahrain',               nameAr: 'البحرين',  currency: 'BHD', lang: 'ar' },
    { code: 'OM', flag: '🇴🇲', nameEn: 'Oman',                  nameAr: 'عُمان',    currency: 'OMR', lang: 'ar' },
    { code: 'QA', flag: '🇶🇦', nameEn: 'Qatar',                 nameAr: 'قطر',      currency: 'QAR', lang: 'ar' },
    { code: 'TR', flag: '🇹🇷', nameEn: 'Turkey',                nameAr: 'تركيا',    currency: 'TRY', lang: 'tr' },
];

const BUSINESS_TYPE_OPTIONS = [
    'Hair salon', 'Beauty salon', 'Spa', 'Barbershop', 'Nail bar',
    'Massage', 'Skin care', 'Brows & lashes', 'Mixed services',
];

/** O3 — display keys for the offline fallback business types (values stay English). */
const BUSINESS_TYPE_FALLBACK_KEYS: Record<string, string> = {
    'Hair salon': 'mb.details.type.hairSalon',
    'Beauty salon': 'mb.details.type.beautySalon',
    'Spa': 'mb.details.type.spa',
    'Barbershop': 'mb.details.type.barbershop',
    'Nail bar': 'mb.details.type.nailBar',
    'Massage': 'mb.details.type.massage',
    'Skin care': 'mb.details.type.skinCare',
    'Brows & lashes': 'mb.details.type.browsLashes',
    'Mixed services': 'mb.details.type.mixed',
};

const BusinessDetailsSection: React.FC = () => {
    const { businessId } = useAuthContext();
    // O3 (SO-38) — every owner-facing string on this page goes through t().
    const { t, language } = useLanguage();
    const errText = (m: string | undefined) => translateValidationMessage(m, t);
    const { data: business, isLoading, isError, refetch } = useBusinessDetailsQuery();
    const updateMutation = useUpdateBusinessMutation(businessId);
    // AB#1682 — master picker (cached server-side 15 min) + holiday CRUD.
    const { data: businessTypesMaster } = useBusinessTypesQuery();
    const createHolidayMutation = useCreateHolidayClosureMutation();
    const updateHolidayMutation = useUpdateHolidayClosureMutation();
    const deleteHolidayMutation = useDeleteHolidayClosureMutation();

    // AB#1984 — A transient `business.me` miss (null/404 that doesn't throw)
    // must not masquerade as "no business yet" and wipe the form. The JWT
    // businessId is the authoritative "owner has a business" signal.
    const { businessLoadFailed, noBusinessYet, canHydrate } = deriveBusinessLoadState({
        isLoading,
        isError,
        business,
        businessId,
    });

    // Type the form with the schema's INPUT type so RHF + zodResolver align;
    // the OUTPUT type (with defaults applied) flows out of `handleSubmit`.
    const form = useForm<BusinessDetailsFormInput, unknown, BusinessDetailsForm>({
        resolver: zodResolver(businessDetailsFormSchema),
        defaultValues: defaultBusinessDetailsForm() as BusinessDetailsFormInput,
        mode: 'onBlur',
    });
    const { control, register, handleSubmit, reset, watch, formState } = form;

    // Seed form from BE response + restore localStorage draft for the
    // Phase-B-deferred fields so the user's previous local edits don't
    // disappear on reload. AB#1984 — only hydrate from a *trustworthy* load;
    // never reset() from a loading / errored / anomalous (JWT says a business
    // exists but the body came back null) response — that is what wiped the
    // saved nameEn during the live incident.
    React.useEffect(() => {
        if (!canHydrate) return;
        const beValues = mapResponseToForm(business ?? null);
        const draft = loadLocalDraft(businessId) ?? {};
        reset({ ...beValues, ...draft });
    }, [business, businessId, canHydrate, reset]);

    // Debounced auto-save of the local-only fields. The BE-supported fields
    // are also written so the draft acts as a transient mirror — if the user
    // reloads before clicking Save, their edits are still there.
    const debouncedSave = useDebouncedSaveLocalDraft(businessId);
    React.useEffect(() => {
        const sub = watch((value) => debouncedSave(value as BusinessDetailsForm));
        return () => sub.unsubscribe();
    }, [watch, debouncedSave]);

    // ── Logo upload (B3) ────────────────────────────────────────────────
    // Reuses /onboarding/documents/upload (Blob-backed since BE PR #598)
    // with documentType=logo. The BE writes a new BusinessDocument row;
    // BusinessDetailsProjection surfaces the latest as logoUrl, which the
    // FE renders below. Invalidate business.me after success so the new
    // logo appears immediately without a manual refresh.
    const qc = useQueryClient();
    const { showToast } = useToast();
    const logoInputRef = React.useRef<HTMLInputElement | null>(null);
    const [logoUploading, setLogoUploading] = React.useState(false);

    const handleLogoUploadClick = React.useCallback(() => {
        if (logoUploading) return;
        if (businessId == null) {
            showToast(t('mb.details.logo.saveFirst'), 'error');
            return;
        }
        logoInputRef.current?.click();
    }, [logoUploading, businessId, showToast, t]);

    const handleLogoFileChange = React.useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        // Reset the input value so picking the same file twice still fires onChange.
        e.target.value = '';
        if (!file || businessId == null) return;

        const MAX = 2 * 1024 * 1024;
        if (file.size > MAX) {
            showToast(t('mb.details.logo.tooLarge'), 'error');
            return;
        }
        if (!/^image\/(png|jpe?g|webp)$/i.test(file.type)) {
            showToast(t('mb.details.logo.badType'), 'error');
            return;
        }

        setLogoUploading(true);
        try {
            await apiService.uploadOnboardingDocument(file, 'logo', businessId);
            await qc.invalidateQueries({ queryKey: queryKeys.business.me() });
            showToast(t('mb.details.logo.uploaded'), 'success');
        } catch {
            showToast(t('mb.details.logo.failed'), 'error');
        } finally {
            setLogoUploading(false);
        }
    }, [businessId, qc, showToast, t]);

    // B15 — Track which brand-asset URLs have failed to load so the FE
    // can swap to a clean empty-state placeholder instead of showing the
    // broken-image alt text. Reset whenever the URL itself changes (post-
    // upload), so a fresh URL gets a clean retry.
    const [logoLoadFailed, setLogoLoadFailed] = React.useState(false);
    const [coverLoadFailed, setCoverLoadFailed] = React.useState(false);
    React.useEffect(() => { setLogoLoadFailed(false); }, [business?.logoUrl]);
    React.useEffect(() => { setCoverLoadFailed(false); }, [business?.coverUrl]);

    const logoUrl = business?.logoUrl ? resolveImageUrl(business.logoUrl) : null;

    // ── Salon card image (cover) upload ─────────────────────────────────
    // Big hero image used on the customer mobile app's salon search cards
    // and the salon detail screen banner. Same Blob pipeline as the logo
    // (documentType=coverPhoto). 5 MB cap + 4:3 guidance — covers tend to
    // be larger than logos and need a horizontal aspect for card layouts.
    const coverInputRef = React.useRef<HTMLInputElement | null>(null);
    const [coverUploading, setCoverUploading] = React.useState(false);

    const handleCoverUploadClick = React.useCallback(() => {
        if (coverUploading) return;
        if (businessId == null) {
            showToast(t('mb.details.cover.saveFirst'), 'error');
            return;
        }
        coverInputRef.current?.click();
    }, [coverUploading, businessId, showToast, t]);

    const handleCoverFileChange = React.useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        e.target.value = '';
        if (!file || businessId == null) return;

        const MAX = 5 * 1024 * 1024;
        if (file.size > MAX) {
            showToast(t('mb.details.cover.tooLarge'), 'error');
            return;
        }
        if (!/^image\/(png|jpe?g|webp)$/i.test(file.type)) {
            showToast(t('mb.details.cover.badType'), 'error');
            return;
        }

        setCoverUploading(true);
        try {
            await apiService.uploadOnboardingDocument(file, 'coverPhoto', businessId);
            await qc.invalidateQueries({ queryKey: queryKeys.business.me() });
            showToast(t('mb.details.cover.uploaded'), 'success');
        } catch {
            showToast(t('mb.details.cover.failed'), 'error');
        } finally {
            setCoverUploading(false);
        }
    }, [businessId, qc, showToast, t]);

    const coverUrl = business?.coverUrl ? resolveImageUrl(business.coverUrl) : null;

    // AB#1842 (PBI-F) — Salon-gender hard-block: state for the
    // BUSINESS_HAS_INCOMPATIBLE_STAFF blocker dialog + Super Admin override
    // request flow. `blockerTarget` is the ServiceType the owner attempted
    // to switch TO; we hold onto it so the dialog title + body interpolate
    // the correct label. `mismatchedStaff` is resolved from the BE-returned
    // `mismatchedStaffIds` against a one-shot staff fetch (no need to keep
    // the staff list permanently in this section's state — flips are rare).
    const navigate = useNavigate();
    const tLang = t;
    const [blockerOpen, setBlockerOpen] = useState(false);
    const [blockerTarget, setBlockerTarget] = useState<ServiceType | null>(null);
    const [mismatchedStaff, setMismatchedStaff] = useState<MismatchedStaff[]>([]);
    const [overrideOpen, setOverrideOpen] = useState(false);
    const [overrideTargetStaff, setOverrideTargetStaff] = useState<MismatchedStaff | null>(null);
    const [overrideSubmitting, setOverrideSubmitting] = useState(false);

    /** Resolve `mismatchedStaffIds` into full row info (name + photo + position
     *  + gender) by fetching the salon-owner staff list once and joining
     *  client-side. The fetch tolerates partial misses (an id not in the
     *  list renders as "Staff #{id}" with no avatar) so the UX never blocks
     *  on a stale cache. */
    const resolveMismatchedStaff = React.useCallback(
        async (ids: readonly number[]): Promise<MismatchedStaff[]> => {
            if (ids.length === 0) return [];
            try {
                const { staff = [] } = await apiService.getBusinessStaff({ pageSize: 50 });
                const byId = new Map<number, BusinessStaffDto>(staff.map(s => [s.id, s]));
                return ids.map(id => {
                    const s = byId.get(id);
                    if (!s) {
                        return {
                            id,
                            fullName: interpolate(t('mb.details.staffFallback'), { id }),
                            position: null,
                            photoUrl: null,
                            // Conservative default: pick the gender that
                            // would have triggered the block. Falls back
                            // to Male if we can't tell from context.
                            gender: 'Male' as const,
                        };
                    }
                    // AB#1861 — BE serializes Gender as a string ("Male" /
                    // "Female") via the global JsonStringEnumConverter
                    // (PBI-B / AB#1840). Accept both the post-PBI-B string
                    // shape AND the int shape so this stays correct whether
                    // or not the audit (AB#1894) has touched this endpoint.
                    const isFemale =
                        s.gender === Gender.Female || s.gender === 'Female';
                    return {
                        id: s.id,
                        fullName: s.fullName ?? interpolate(t('mb.details.staffFallback'), { id }),
                        position: s.position ?? null,
                        photoUrl: s.photoUrl ?? null,
                        gender: isFemale ? 'Female' as const : 'Male' as const,
                    };
                });
            } catch {
                // Fall back to id-only rows so the owner still sees WHO is
                // blocking, even if the staff list is unreachable.
                return ids.map(id => ({
                    id,
                    fullName: interpolate(t('mb.details.staffFallback'), { id }),
                    position: null,
                    photoUrl: null,
                    gender: 'Male' as const,
                }));
            }
        },
        [t],
    );

    const onSubmit = handleSubmit(async (data) => {
        // Mirror to localStorage as a transient client-side cache so a
        // browser reload after a save error doesn't lose user edits.
        saveLocalDraft(businessId, data);
        if (businessId == null) {
            // No business yet — Phase A doesn't expose the create flow.
            return;
        }
        // AB#1682 — pass the BusinessTypes master so name → id resolution
        // works. If the master is still loading, businessTypeIds is omitted
        // and BE treats null as "no change" (preserves existing selection).
        // AB#1682 hotfix — pass the original GET response so the adapter
        // can echo city/state/zipCode/streetAddress/categoryIds the form
        // doesn't manage today (BE has [Required] on those).
        const payload = mapFormToUpdateRequest(data, businessTypesMaster, business);
        // AB#1842 (PBI-F) — derive the target ServiceType from the same
        // resolver the BE will see, so when the BE rejects we know which
        // value the owner tried to flip to (for the dialog title).
        const targetServiceType = payload.serviceType as ServiceType;

        updateMutation.mutate(payload, {
            onError: async (err) => {
                // The shared mutation onError already fires a toast; here
                // we additionally hijack the structured 400 for the salon-
                // hard-block case to open the blocker dialog.
                // AB#1919 (WS-5b) — top-level `code` via normalizer (`errorCode
                // ?? code`); the nested structured `data.data.mismatchedStaffIds`
                // payload is domain-specific and read directly.
                const e = err as
                    & { response?: { data?: { data?: { mismatchedStaffIds?: number[] } } } };
                const normalizedCode = normalizeApiError(err).code;
                const code = normalizedCode !== 'UNKNOWN' ? normalizedCode : undefined;
                const ids = e?.response?.data?.data?.mismatchedStaffIds;
                if (code === 'BUSINESS_HAS_INCOMPATIBLE_STAFF' && Array.isArray(ids) && ids.length > 0) {
                    const resolved = await resolveMismatchedStaff(ids);
                    setMismatchedStaff(resolved);
                    setBlockerTarget(targetServiceType);
                    setBlockerOpen(true);
                }
            },
        });
    });

    const handleBlockerCancel = React.useCallback(() => {
        setBlockerOpen(false);
        setMismatchedStaff([]);
        setBlockerTarget(null);
    }, []);

    const handleBlockerResolve = React.useCallback(() => {
        handleBlockerCancel();
        navigate('/my-business/staff');
    }, [handleBlockerCancel, navigate]);

    /** Open the Super Admin override request for the FIRST mismatched staff
     *  in the list. Per Faris brief §D5: each override approves ONE staff,
     *  so an owner with 3 blockers makes 3 requests. The UX surfaces this
     *  by opening the override modal pre-targeted at the first row; the
     *  blocker dialog stays in the background — when the owner finishes one
     *  request, the modal closes and the dialog still lists the remaining
     *  blockers so they can submit more. */
    const handleRequestOverride = React.useCallback(() => {
        const first = mismatchedStaff[0];
        if (!first) return;
        setOverrideTargetStaff(first);
        setOverrideOpen(true);
    }, [mismatchedStaff]);

    const handleOverrideCancel = React.useCallback(() => {
        setOverrideOpen(false);
        setOverrideTargetStaff(null);
    }, []);

    const handleOverrideSubmit = React.useCallback(
        async (justification: string) => {
            if (businessId == null || overrideTargetStaff == null) return;
            setOverrideSubmitting(true);
            try {
                await apiService.requestGenderOverride(
                    businessId,
                    overrideTargetStaff.id,
                    { justification },
                );
                showToast(tLang('business.serviceType.flip.override.submitted'), 'success');
                setOverrideOpen(false);
                setOverrideTargetStaff(null);
                // Remove the just-requested staff from the visible blocker
                // list so the owner can move on to the next one (or close
                // the dialog if nothing else is blocking). The BE keeps
                // them blocked until Super Admin Approves; this is purely
                // a UX nicety.
                setMismatchedStaff(prev => prev.filter(s => s.id !== overrideTargetStaff.id));
            } catch {
                showToast(tLang('business.serviceType.flip.override.error'), 'error');
            } finally {
                setOverrideSubmitting(false);
            }
        },
        [businessId, overrideTargetStaff, showToast, tLang],
    );

    return (
        <>
        <form onSubmit={onSubmit} noValidate>
            <header className={styles.header}>
                <h1>{t('mb.details.title')}</h1>
                <p>{t('mb.details.subtitle')}</p>
            </header>

            <div className={styles.intro}>
                <span className={styles.introIcon}>💡</span>
                <div>
                    <strong>{t('mb.details.intro.strong')}</strong> {t('mb.details.intro.body')}
                </div>
            </div>

            {isLoading && (
                <div className={styles.intro}>
                    <span className={styles.introIcon}>⏳</span>
                    <div>{t('mb.details.loading')}</div>
                </div>
            )}

            {(isError || businessLoadFailed) && (
                <div className={styles.intro} role="alert">
                    <span className={styles.introIcon}>⚠️</span>
                    <div>
                        <strong>{t('mb.details.loadFailed')}</strong>{' '}
                        {isError
                            ? t('mb.details.loadFailedRetry')
                            : t('mb.details.loadFailedTemporary')}
                        {' '}
                        <button
                            type="button"
                            className={styles.btn}
                            onClick={() => { void refetch(); }}
                            style={{ marginInlineStart: 8 }}
                        >
                            {t('mb.common.retry')}
                        </button>
                    </div>
                </div>
            )}

            {noBusinessYet && (
                <div className={styles.intro}>
                    <span className={styles.introIcon}>🆕</span>
                    <div>
                        <strong>{t('mb.details.noBusiness.strong')}</strong> {t('mb.details.noBusiness.body')}
                    </div>
                </div>
            )}

            {/* CR-2026-05-31-a + CR-2026-05-31-c — Mandatory-fields banner
                Fires when AR name OR EN about OR AR about are missing/short.
                Save button on every card is disabled by RHF when the form
                fails validation; this banner explains WHY upfront so the
                user doesn't hunt for the field. */}
            {(() => {
                const watchedNameAr = watch('nameAr') ?? '';
                const watchedAbout = watch('about') ?? '';
                const watchedAboutAr = watch('aboutAr') ?? '';
                const missing: string[] = [];
                if (watchedNameAr.trim().length < 2) missing.push(t('mb.details.missing.nameAr'));
                if (watchedAbout.trim().length < 50) missing.push(t('mb.details.missing.aboutEn'));
                if (watchedAboutAr.trim().length < 50) missing.push(t('mb.details.missing.aboutAr'));
                if (missing.length === 0) return null;
                return (
                    <div
                        role="alert"
                        style={{
                            display: 'flex',
                            gap: 12,
                            padding: '12px 16px',
                            margin: '12px 0 16px',
                            borderRadius: 10,
                            border: '1px solid var(--warning-border)',
                            background: 'var(--warning-tint)',
                            color: 'var(--warning-fg)',
                            alignItems: 'flex-start',
                        }}
                    >
                        <span style={{ fontSize: 18, lineHeight: '20px' }} aria-hidden="true">⚠️</span>
                        <div>
                            <strong style={{ color: 'var(--warning-fg)', display: 'block', marginBottom: 4 }}>
                                {t('mb.details.missing.title')}
                            </strong>
                            <ul style={{ margin: 0, paddingInlineStart: 18, color: 'var(--text-secondary)' }}>
                                {missing.map(m => <li key={m}>{m}</li>)}
                            </ul>
                        </div>
                    </div>
                );
            })()}

            {/* Item #1 (CR-2026-05-31 batch) — Single "Brand assets" card combining
                Business Logo + Salon card image as compact side-by-side tiles.
                Was two full-width cards (~480px vertical) → now one card (~180px)
                so the form fields are visible without scrolling. */}
            <div className={styles.card}>
                <div className={styles.cardTitle}>{t('mb.details.brand.title')}</div>
                <div className={styles.cardSub}>{t('mb.details.brand.sub')}</div>
                <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', marginTop: 12 }}>
                    {/* Logo tile */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                        <span style={{ fontSize: 12, color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: language === 'ar' ? 0 : '0.06em', fontWeight: 600 }}>
                            {t('mb.details.brand.logo')}
                        </span>
                        <div
                            className={styles.logoDrop}
                            aria-hidden={logoUrl && !logoLoadFailed ? undefined : true}
                            style={{ width: 96, height: 96 }}
                            title={logoUrl ? t('mb.details.brand.replaceHint') : t('mb.details.brand.logoUploadHint')}
                        >
                            {logoUrl && !logoLoadFailed ? (
                                <img
                                    src={logoUrl}
                                    alt=""
                                    onError={() => setLogoLoadFailed(true)}
                                    style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 'inherit' }}
                                />
                            ) : '📷'}
                        </div>
                        <input
                            ref={logoInputRef}
                            type="file"
                            accept="image/png,image/jpeg,image/webp"
                            style={{ display: 'none' }}
                            onChange={handleLogoFileChange}
                            aria-hidden="true"
                            tabIndex={-1}
                        />
                        <button
                            type="button"
                            className={styles.btn}
                            onClick={handleLogoUploadClick}
                            disabled={logoUploading || businessId == null}
                            style={{ padding: '6px 10px', fontSize: 12.5, minWidth: 96 }}
                        >
                            {logoUploading ? t('mb.common.uploading') : logoUrl ? t('mb.common.replace') : t('mb.common.upload')}
                        </button>
                        <span style={{ fontSize: 10.5, color: 'var(--text-tertiary)' }}>
                            {t('mb.details.brand.logoSpec')}
                        </span>
                    </div>

                    {/* Cover tile */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                        <span style={{ fontSize: 12, color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: language === 'ar' ? 0 : '0.06em', fontWeight: 600 }}>
                            {t('mb.details.brand.cover')}
                        </span>
                        <div
                            className={styles.logoDrop}
                            aria-hidden={coverUrl && !coverLoadFailed ? undefined : true}
                            style={{ width: 160, height: 120 }}
                            title={coverUrl ? t('mb.details.brand.replaceHint') : t('mb.details.brand.coverUploadHint')}
                        >
                            {coverUrl && !coverLoadFailed ? (
                                <img
                                    src={coverUrl}
                                    alt=""
                                    onError={() => setCoverLoadFailed(true)}
                                    style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 'inherit' }}
                                />
                            ) : '🖼️'}
                        </div>
                        <input
                            ref={coverInputRef}
                            type="file"
                            accept="image/png,image/jpeg,image/webp"
                            style={{ display: 'none' }}
                            onChange={handleCoverFileChange}
                            aria-hidden="true"
                            tabIndex={-1}
                        />
                        <button
                            type="button"
                            className={styles.btn}
                            onClick={handleCoverUploadClick}
                            disabled={coverUploading || businessId == null}
                            style={{ padding: '6px 10px', fontSize: 12.5, minWidth: 96 }}
                        >
                            {coverUploading ? t('mb.common.uploading') : coverUrl ? t('mb.common.replace') : t('mb.common.upload')}
                        </button>
                        <span style={{ fontSize: 10.5, color: 'var(--text-tertiary)' }}>
                            {t('mb.details.brand.coverSpec')}
                        </span>
                    </div>
                </div>
            </div>

                        <div className={styles.card}>
                <div className={styles.cardTitle}>{t('mb.details.info.title')}</div>
                <div className={styles.cardSub}>{t('mb.details.info.sub')}</div>

                <div className={styles.formGrid}>
                    <div className={styles.field}>
                        <label htmlFor="bd-name">{t('mb.details.info.nameEn')}</label>
                        <input
                            id="bd-name"
                            type="text"
                            dir="ltr"
                            placeholder="e.g. Devanza Beauty Lounge"
                            {...register('name')}
                        />
                        {formState.errors.name && (
                            <span className={styles.fieldError}>{errText(formState.errors.name.message)}</span>
                        )}
                    </div>
                    <div className={`${styles.field} ${styles.fieldRtl}`}>
                        {/* CR-2026-05-31-a (AB#1825) — AR business name is mandatory */}
                        <label htmlFor="bd-nameAr">
                            {t('mb.details.info.nameAr')} <span style={{ color: 'var(--destructive-fg)', fontWeight: 700 }} aria-hidden="true">*</span>
                        </label>
                        <input
                            id="bd-nameAr"
                            type="text"
                            placeholder="مثال: استوديو ديفانزا"
                            {...register('nameAr')}
                        />
                        {formState.errors.nameAr && (
                            <span className={styles.fieldError}>{errText(formState.errors.nameAr.message)}</span>
                        )}
                    </div>
                    <div className={styles.field}>
                        <label>
                            {t('mb.details.info.type')} <span className={styles.fieldHint}>{t('mb.details.info.typeHint')}</span>
                        </label>
                        <Controller
                            control={control}
                            name="businessTypes"
                            render={({ field }) => (
                                <MultiSelectChips
                                    // AB#1682 — picker options come from the BE
                                    // BusinessTypes master via useBusinessTypesQuery
                                    // (cached 15 min). Falls back to the static list
                                    // while the master is loading or offline.
                                    options={(businessTypesMaster ?? []).length > 0
                                        ? businessTypesMaster!.map(t => t.nameEn)
                                        : BUSINESS_TYPE_OPTIONS}
                                    maxSelected={4}
                                    placeholder={t('mb.details.info.typePlaceholder')}
                                    getLabel={(opt) => {
                                        const m = (businessTypesMaster ?? []).find(bt => bt.nameEn === opt);
                                        if (m) return language === 'ar' ? (m.nameAr || m.nameEn) : m.nameEn;
                                        const k = BUSINESS_TYPE_FALLBACK_KEYS[opt];
                                        return k ? t(k) : opt;
                                    }}
                                    value={field.value ?? []}
                                    onChange={field.onChange}
                                />
                            )}
                        />
                    </div>
                    <div className={styles.field}>
                        <label htmlFor="bd-country">{t('mb.branchForm.country')}</label>
                        <Controller
                            control={control}
                            name="country"
                            render={({ field }) => (
                                <select id="bd-country" value={field.value} onChange={field.onChange}>
                                    {COUNTRIES.map(c => (
                                        <option key={c.code} value={c.code}>
                                            {c.flag} {language === 'ar' ? c.nameAr : c.nameEn} · {c.currency}
                                        </option>
                                    ))}
                                </select>
                            )}
                        />
                    </div>
                </div>
            </div>

            {/*
              * Epic AB#1824 — Hours single-editor model (Model E).
              *
              * The legacy `<BusinessHours>` editor was decommissioned in
              * favour of the new BusinessHoursMatrix on the dedicated
              * `Hours` tab (route: `/my-business/business-details?tab=hours`).
              * The matrix writes per-branch `BranchHours` rows directly via
              * `PUT /api/business/hours/bulk` — see Faris arch brief at
              * `devanza-claude/docs/features/AB1820/arch-brief.md`.
              *
              * The BusinessDetails save flow no longer touches BusinessHours.
              * The `businessHours` form field stays in the RHF schema so the
              * existing PUT shape doesn't change, but its values are seeded
              * from the BE response and re-saved unmodified — the matrix
              * owns Hours editing end-to-end.
              */}
            <div className={styles.card}>
                <div className={styles.cardTitle}>{t('mb.details.hours.title')}</div>
                <div className={styles.cardSub}>{t('mb.details.hours.sub')}</div>
                <BusinessHoursMatrix salonName={business?.name ?? ''} />
            </div>

            <div className={styles.card}>
                <div className={styles.cardTitle}>{t('mb.details.holidays.title')}</div>
                <div className={styles.cardSub}>{t('mb.details.holidays.sub')}</div>
                <Controller
                    control={control}
                    name="holidays"
                    render={({ field }) => (
                        <HolidayClosures
                            country={watch('country')}
                            value={(field.value ?? []) as HolidayClosureForm[]}
                            onChange={field.onChange}
                            // AB#1682 — instant per-row CRUD against BE
                            // (independent of the form Save button).
                            onAdd={async (closure) => {
                                const created = await createHolidayMutation.mutateAsync({
                                    fromDate: closure.from,
                                    toDate: closure.to,
                                    reason: closure.reason,
                                });
                                return String(created.id);
                            }}
                            onUpdate={async (closure) => {
                                if (!/^\d+$/.test(closure.id)) return; // local-only row not yet persisted
                                await updateHolidayMutation.mutateAsync({
                                    id: Number(closure.id),
                                    fromDate: closure.from,
                                    toDate: closure.to,
                                    reason: closure.reason,
                                });
                            }}
                            onRemove={async (id) => {
                                if (!/^\d+$/.test(id)) return; // never persisted; local removal is enough
                                await deleteHolidayMutation.mutateAsync(Number(id));
                            }}
                        />
                    )}
                />
            </div>

            {/* CR-2026-05-31-c (AB#1826) — About is mandatory in BOTH languages.
                Min 50 chars per side. Character counter visible while typing so
                the owner sees their progress. Asterisks on the labels. Error
                messages render below each field when min not met. */}
            <div className={styles.card}>
                <div className={styles.cardTitle}>{t('mb.details.about.title')}</div>
                <div className={styles.cardSub}>{t('mb.details.about.sub')}</div>
                <div className={styles.formGrid}>
                    <div className={styles.field}>
                        <label htmlFor="bd-about">
                            {t('mb.details.about.en')} <span style={{ color: 'var(--destructive-fg)', fontWeight: 700 }} aria-hidden="true">*</span>
                        </label>
                        <textarea
                            id="bd-about"
                            dir="ltr"
                            placeholder="A few sentences about what makes your salon special…"
                            {...register('about')}
                        />
                        {(() => {
                            const len = (watch('about') ?? '').trim().length;
                            const ok = len >= 50;
                            return (
                                <span
                                    className={styles.fieldHint}
                                    style={{
                                        fontSize: 11,
                                        color: ok ? 'var(--success-fg)' : 'var(--text-tertiary)',
                                        marginBlockStart: 4,
                                    }}
                                >
                                    {interpolate(t('mb.details.about.counter'), { n: len })}{ok ? ' ✓' : ''}
                                </span>
                            );
                        })()}
                        {formState.errors.about && (
                            <span className={styles.fieldError}>{errText(formState.errors.about.message)}</span>
                        )}
                    </div>
                    <div className={`${styles.field} ${styles.fieldRtl}`}>
                        <label htmlFor="bd-aboutAr">
                            {t('mb.details.about.ar')} <span style={{ color: 'var(--destructive-fg)', fontWeight: 700 }} aria-hidden="true">*</span>
                        </label>
                        <textarea
                            id="bd-aboutAr"
                            placeholder="بضع جمل عن ما يميز صالونك…"
                            {...register('aboutAr')}
                        />
                        {(() => {
                            const len = (watch('aboutAr') ?? '').trim().length;
                            const ok = len >= 50;
                            return (
                                <span
                                    className={styles.fieldHint}
                                    style={{
                                        fontSize: 11,
                                        color: ok ? 'var(--success-fg)' : 'var(--text-tertiary)',
                                        marginBlockStart: 4,
                                    }}
                                >
                                    {interpolate(t('mb.details.about.counter'), { n: len })}{ok ? ' ✓' : ''}
                                </span>
                            );
                        })()}
                        {formState.errors.aboutAr && (
                            <span className={styles.fieldError}>{errText(formState.errors.aboutAr.message)}</span>
                        )}
                    </div>
                </div>
            </div>

            <div className={styles.card}>
                <div className={styles.cardTitle}>{t('mb.details.contact.title')}</div>
                <div className={styles.cardSub}>{t('mb.details.contact.sub')}</div>
                <div className={styles.formGrid}>
                    <div className={styles.field}>
                        <label htmlFor="bd-email">{t('mb.details.contact.email')}</label>
                        <input id="bd-email" type="email" dir="ltr" placeholder="hello@yoursalon.com" {...register('email')} />
                        {formState.errors.email && (
                            <span className={styles.fieldError}>{errText(formState.errors.email.message)}</span>
                        )}
                    </div>
                    <div className={styles.field}>
                        <label htmlFor="bd-phone">{t('mb.details.contact.phone')}</label>
                        <input id="bd-phone" type="tel" dir="ltr" placeholder="+971 50 123 4567" {...register('phone')} />
                    </div>
                    <div className={styles.field}>
                        <label htmlFor="bd-website">{t('mb.details.contact.website')}</label>
                        <input id="bd-website" type="url" dir="ltr" placeholder="https://…" {...register('website')} />
                    </div>
                </div>
            </div>

            <div className={styles.card}>
                <div className={styles.cardTitle}>{t('mb.details.social.title')}</div>
                <div className={styles.cardSub}>{t('mb.details.social.sub')}</div>
                <div className={styles.socialList}>
                    <SocialInput icon={<FacebookIcon />}  placeholder="https://facebook.com/yoursalon"  {...register('social.facebook')} />
                    <SocialInput icon={<TwitterIcon />}   placeholder="https://twitter.com/yoursalon"   {...register('social.twitter')} />
                    <SocialInput icon={<InstagramIcon />} placeholder="https://instagram.com/yoursalon" {...register('social.instagram')} />
                    <SocialInput icon={<WhatsAppIcon />}  placeholder={t('mb.details.social.whatsapp')}     {...register('social.whatsapp')} />
                    <SocialInput icon={<TikTokIcon />}    placeholder="https://tiktok.com/@yoursalon"   {...register('social.tiktok')} />
                    <SocialInput icon={<MapPinIcon />}    placeholder={t('mb.details.social.maps')}    {...register('social.googleMaps')} />
                </div>
            </div>

            <div className={styles.card}>
                <div className={styles.cardTitle}>{t('mb.details.other.title')}</div>
                <div className={styles.cardSub}>{t('mb.details.other.sub')}</div>
                <Controller
                    control={control}
                    name="settings"
                    render={({ field }) => (
                        <OtherSettingsBlock
                            value={(field.value ?? {}) as OtherSettingsForm}
                            onChange={field.onChange}
                        />
                    )}
                />

                <div className={styles.saveRow}>
                    <span className={styles.saveHint}>
                        {updateMutation.isPending
                            ? t('mb.common.saving')
                            : t('mb.details.saveHint')}
                    </span>
                    <button
                        type="submit"
                        className={styles.btnPrimary}
                        disabled={updateMutation.isPending || isLoading}
                    >
                        {updateMutation.isPending ? t('mb.common.saving') : t('mb.common.saveChanges')}
                    </button>
                </div>
            </div>
        </form>
        {/* AB#1842 (PBI-F) — Mismatched-staff blocker + Super Admin override
            request flow. Mounted outside the form so a save attempt that
            triggers the BE's BUSINESS_HAS_INCOMPATIBLE_STAFF code (i.e.,
            flipping ServiceType while incompatible staff exist) opens the
            blocker dialog. Cancel keeps the form state intact (the BE
            rejected, so the salon's ServiceType didn't change). Resolve
            takes the owner to /my-business/staff. The override link spawns
            the textarea modal. */}
        {blockerOpen && blockerTarget != null && (
            <StaffMismatchBlockingDialog
                isOpen={blockerOpen}
                targetServiceType={blockerTarget}
                mismatchedStaff={mismatchedStaff}
                onCancel={handleBlockerCancel}
                onResolve={handleBlockerResolve}
                onRequestOverride={handleRequestOverride}
            />
        )}
        {overrideOpen && overrideTargetStaff && (
            <SuperAdminOverrideRequestModal
                isOpen={overrideOpen}
                staffName={overrideTargetStaff.fullName}
                isSubmitting={overrideSubmitting}
                onCancel={handleOverrideCancel}
                onSubmit={handleOverrideSubmit}
            />
        )}
        </>
    );
};

export default BusinessDetailsSection;

/* ── Sub-components (controlled) ────────────────────────────────────────── */

interface MultiSelectChipsProps {
    options: string[];
    maxSelected?: number;
    placeholder?: string;
    value: string[];
    onChange: (next: string[]) => void;
    /** O3 — display label for an option value (values stay as stored). */
    getLabel?: (opt: string) => string;
}

/**
 * AB#1675 — Multi-select chips control. Click to open a dropdown of
 * options; chosen options surface as removable chips inside the field.
 * Caps selection at `maxSelected` (defaults to 4) and surfaces a hint
 * once the cap is reached. Fully controlled.
 */
const MultiSelectChips: React.FC<MultiSelectChipsProps> = ({
    options, maxSelected = 4, placeholder, value, onChange, getLabel,
}) => {
    const { t } = useLanguage();
    const label = (opt: string) => (getLabel ? getLabel(opt) : opt);
    const [open, setOpen] = React.useState(false);
    const wrapRef = React.useRef<HTMLDivElement>(null);

    React.useEffect(() => {
        if (!open) return;
        const onDocClick = (e: MouseEvent) => {
            if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
        };
        document.addEventListener('mousedown', onDocClick);
        return () => document.removeEventListener('mousedown', onDocClick);
    }, [open]);

    const toggle = (opt: string) => {
        if (value.includes(opt)) {
            onChange(value.filter(x => x !== opt));
            return;
        }
        if (value.length >= maxSelected) return;
        onChange([...value, opt]);
    };

    const remove = (opt: string, e: React.MouseEvent) => {
        e.stopPropagation();
        onChange(value.filter(x => x !== opt));
    };

    const atCap = value.length >= maxSelected;

    return (
        <div className={styles.multiSelect} ref={wrapRef}>
            <button
                type="button"
                className={`${styles.multiSelectField} ${open ? styles.multiSelectOpen : ''}`}
                onClick={() => setOpen(o => !o)}
                aria-haspopup="listbox"
                aria-expanded={open}
            >
                {value.length === 0 ? (
                    <span className={styles.multiSelectPlaceholder}>{placeholder ?? t('mb.details.info.typePlaceholder')}</span>
                ) : (
                    <span className={styles.chipList}>
                        {value.map(opt => (
                            <span key={opt} className={styles.chip}>
                                {label(opt)}
                                <span
                                    role="button"
                                    aria-label={interpolate(t('mb.details.info.removeType'), { name: label(opt) })}
                                    className={styles.chipRemove}
                                    onClick={(e) => remove(opt, e)}
                                >
                                    ×
                                </span>
                            </span>
                        ))}
                    </span>
                )}
                <svg className={styles.multiSelectCaret} width="12" height="8" viewBox="0 0 12 8" aria-hidden="true">
                    <path d="M1 1L6 6L11 1" stroke="currentColor" strokeWidth="1.6" fill="none" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
            </button>

            {open && (
                <div className={styles.multiSelectMenu} role="listbox">
                    {options.map(opt => {
                        const isSelected = value.includes(opt);
                        const isDisabled = !isSelected && atCap;
                        return (
                            <button
                                key={opt}
                                type="button"
                                role="option"
                                aria-selected={isSelected}
                                disabled={isDisabled}
                                className={`${styles.multiSelectOption} ${isSelected ? styles.multiSelectOptionSelected : ''}`}
                                onClick={() => toggle(opt)}
                            >
                                <span className={`${styles.checkBox} ${isSelected ? styles.checkBoxOn : ''}`} aria-hidden="true">
                                    {isSelected && (
                                        <svg width="10" height="10" viewBox="0 0 12 12" fill="none">
                                            <path d="M2.5 6.5L5 9L9.5 3.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                        </svg>
                                    )}
                                </span>
                                {label(opt)}
                            </button>
                        );
                    })}
                    <div className={styles.multiSelectFooter}>
                        {atCap
                            ? interpolate(t('mb.details.info.typeAtCap'), { max: maxSelected })
                            : interpolate(t('mb.details.info.typeCount'), { n: value.length, max: maxSelected })}
                    </div>
                </div>
            )}
        </div>
    );
};

/*
 * AB#1824 — Legacy <BusinessHours> sub-component DECOMMISSIONED.
 *
 * The inline Business Hours editor that used to live here was removed when
 * the single-editor matrix on `Hours` tab shipped. Its keys (WEEK_DAYS,
 * BusinessHoursProps, BusinessHours) are no longer referenced anywhere in
 * this file or the wider codebase — the matrix component owns the surface.
 */

/* ── Holiday closures (controlled) ──────────────────────────────────────── */

const newId = () =>
    typeof crypto !== 'undefined' && 'randomUUID' in crypto
        ? crypto.randomUUID()
        : Math.random().toString(36).slice(2);

const newClosure = (): HolidayClosureForm => {
    const today = new Date().toISOString().slice(0, 10);
    return { id: newId(), from: today, to: today, reason: '' };
};

type SeedHoliday = Omit<HolidayClosureForm, 'id'>;

const REGIONAL_HOLIDAYS_2026: Record<string, SeedHoliday[]> = {
    AE: [
        { from: '2026-01-01', to: '2026-01-01', reason: "New Year's Day" },
        { from: '2026-03-20', to: '2026-03-22', reason: 'Eid Al Fitr (tentative)' },
        { from: '2026-05-26', to: '2026-05-26', reason: 'Arafat Day (tentative)' },
        { from: '2026-05-27', to: '2026-05-29', reason: 'Eid Al Adha (tentative)' },
        { from: '2026-06-16', to: '2026-06-16', reason: 'Hijri New Year (tentative)' },
        { from: '2026-08-25', to: '2026-08-25', reason: "Prophet's Birthday (tentative)" },
        { from: '2026-12-01', to: '2026-12-01', reason: 'Commemoration Day' },
        { from: '2026-12-02', to: '2026-12-03', reason: 'UAE National Day' },
    ],
    JO: [
        { from: '2026-01-01', to: '2026-01-01', reason: "New Year's Day" },
        { from: '2026-03-20', to: '2026-03-22', reason: 'Eid Al Fitr (tentative)' },
        { from: '2026-05-01', to: '2026-05-01', reason: 'Labour Day' },
        { from: '2026-05-25', to: '2026-05-25', reason: 'Independence Day' },
        { from: '2026-05-27', to: '2026-05-29', reason: 'Eid Al Adha (tentative)' },
        { from: '2026-06-16', to: '2026-06-16', reason: 'Hijri New Year (tentative)' },
        { from: '2026-08-25', to: '2026-08-25', reason: "Prophet's Birthday (tentative)" },
        { from: '2026-12-25', to: '2026-12-25', reason: 'Christmas Day' },
    ],
    SA: [
        { from: '2026-02-22', to: '2026-02-22', reason: 'Founding Day' },
        { from: '2026-03-20', to: '2026-03-22', reason: 'Eid Al Fitr (tentative)' },
        { from: '2026-05-27', to: '2026-05-29', reason: 'Eid Al Adha (tentative)' },
        { from: '2026-09-23', to: '2026-09-23', reason: 'National Day' },
    ],
    EG: [
        { from: '2026-01-07', to: '2026-01-07', reason: 'Coptic Christmas' },
        { from: '2026-01-25', to: '2026-01-25', reason: 'Revolution Day' },
        { from: '2026-03-20', to: '2026-03-22', reason: 'Eid Al Fitr (tentative)' },
        { from: '2026-04-25', to: '2026-04-25', reason: 'Sinai Liberation Day' },
        { from: '2026-05-01', to: '2026-05-01', reason: 'Labour Day' },
        { from: '2026-05-27', to: '2026-05-29', reason: 'Eid Al Adha (tentative)' },
        { from: '2026-06-30', to: '2026-06-30', reason: '30 June Revolution' },
        { from: '2026-07-23', to: '2026-07-23', reason: 'Revolution Day' },
        { from: '2026-10-06', to: '2026-10-06', reason: 'Armed Forces Day' },
    ],
    KW: [
        { from: '2026-01-01', to: '2026-01-01', reason: "New Year's Day" },
        { from: '2026-02-25', to: '2026-02-26', reason: 'National Day & Liberation Day' },
        { from: '2026-03-20', to: '2026-03-22', reason: 'Eid Al Fitr (tentative)' },
        { from: '2026-05-27', to: '2026-05-29', reason: 'Eid Al Adha (tentative)' },
    ],
    BH: [
        { from: '2026-01-01', to: '2026-01-01', reason: "New Year's Day" },
        { from: '2026-03-20', to: '2026-03-22', reason: 'Eid Al Fitr (tentative)' },
        { from: '2026-05-01', to: '2026-05-01', reason: 'Labour Day' },
        { from: '2026-05-27', to: '2026-05-29', reason: 'Eid Al Adha (tentative)' },
        { from: '2026-12-16', to: '2026-12-17', reason: 'National Day' },
    ],
    OM: [
        { from: '2026-03-20', to: '2026-03-22', reason: 'Eid Al Fitr (tentative)' },
        { from: '2026-05-27', to: '2026-05-29', reason: 'Eid Al Adha (tentative)' },
        { from: '2026-07-23', to: '2026-07-23', reason: 'Renaissance Day' },
        { from: '2026-11-18', to: '2026-11-18', reason: 'National Day' },
    ],
    QA: [
        { from: '2026-02-10', to: '2026-02-10', reason: 'National Sports Day' },
        { from: '2026-03-20', to: '2026-03-22', reason: 'Eid Al Fitr (tentative)' },
        { from: '2026-05-27', to: '2026-05-29', reason: 'Eid Al Adha (tentative)' },
        { from: '2026-12-18', to: '2026-12-18', reason: 'National Day' },
    ],
    TR: [
        { from: '2026-01-01', to: '2026-01-01', reason: "New Year's Day" },
        { from: '2026-04-23', to: '2026-04-23', reason: "National Sovereignty & Children's Day" },
        { from: '2026-05-01', to: '2026-05-01', reason: 'Labour Day' },
        { from: '2026-05-19', to: '2026-05-19', reason: 'Atatürk Commemoration & Youth Day' },
        { from: '2026-03-20', to: '2026-03-22', reason: 'Ramazan Bayramı (tentative)' },
        { from: '2026-05-27', to: '2026-05-30', reason: 'Kurban Bayramı (tentative)' },
        { from: '2026-08-30', to: '2026-08-30', reason: 'Victory Day' },
        { from: '2026-10-29', to: '2026-10-29', reason: 'Republic Day' },
    ],
};

const COUNTRY_NAME_EN: Record<string, string> = {
    AE: 'UAE', JO: 'Jordan', SA: 'Saudi Arabia', EG: 'Egypt', KW: 'Kuwait',
    BH: 'Bahrain', OM: 'Oman', QA: 'Qatar', TR: 'Turkey',
};

/** O3 — Arabic-UI owners get the seeded holiday names in Arabic. */
const HOLIDAY_REASON_KEYS: Record<string, string> = {
    "New Year's Day": 'mb.holiday.newYear',
    'Eid Al Fitr (tentative)': 'mb.holiday.eidFitr',
    'Arafat Day (tentative)': 'mb.holiday.arafat',
    'Eid Al Adha (tentative)': 'mb.holiday.eidAdha',
    'Hijri New Year (tentative)': 'mb.holiday.hijriNewYear',
    "Prophet's Birthday (tentative)": 'mb.holiday.prophetBirthday',
    'Commemoration Day': 'mb.holiday.commemoration',
    'UAE National Day': 'mb.holiday.uaeNational',
    'Labour Day': 'mb.holiday.labour',
    'Independence Day': 'mb.holiday.independence',
    'Christmas Day': 'mb.holiday.christmas',
    'Founding Day': 'mb.holiday.founding',
    'National Day': 'mb.holiday.national',
    'Coptic Christmas': 'mb.holiday.copticChristmas',
    'Revolution Day': 'mb.holiday.revolution',
    'Sinai Liberation Day': 'mb.holiday.sinai',
    '30 June Revolution': 'mb.holiday.june30',
    'Armed Forces Day': 'mb.holiday.armedForces',
    'National Day & Liberation Day': 'mb.holiday.nationalLiberation',
    'Renaissance Day': 'mb.holiday.renaissance',
    'National Sports Day': 'mb.holiday.sports',
};

interface HolidayClosuresProps {
    country: string;
    value: HolidayClosureForm[];
    onChange: (next: HolidayClosureForm[]) => void;
    /** AB#1682 — instant per-row CRUD against BE.
     *  - onAdd: returns the BE-assigned id (string) so the local row can
     *    swap its temp uuid for the persisted id.
     *  - onUpdate / onRemove: no-op for rows whose id isn't a numeric BE
     *    id (e.g. just-added rows that haven't completed their POST yet). */
    onAdd?: (closure: HolidayClosureForm) => Promise<string | void>;
    onUpdate?: (closure: HolidayClosureForm) => Promise<void>;
    onRemove?: (id: string) => Promise<void>;
}

const HolidayClosures: React.FC<HolidayClosuresProps> = ({
    country, value, onChange, onAdd, onUpdate, onRemove,
}) => {
    const { t, language } = useLanguage();
    const [dismissedFor, setDismissedFor] = React.useState<string | null>(null);
    React.useEffect(() => { setDismissedFor(null); }, [country]);

    const seeds = REGIONAL_HOLIDAYS_2026[country] ?? [];
    const showSeedBanner = seeds.length > 0 && dismissedFor !== country && value.length < 2;

    const loadSeeds = async () => {
        const existingKeys = new Set(value.map(c => `${c.from}|${c.to}`));
        const seedRows: HolidayClosureForm[] = seeds
            .filter(s => !existingKeys.has(`${s.from}|${s.to}`))
            .map(s => ({
                id: newId(),
                ...s,
                reason: language === 'ar' && HOLIDAY_REASON_KEYS[s.reason]
                    ? t(HOLIDAY_REASON_KEYS[s.reason])
                    : s.reason,
            }));
        // Optimistic merge into form state so the UI updates immediately.
        const merged = [...value, ...seedRows].sort((a, b) => a.from.localeCompare(b.from));
        onChange(merged);
        setDismissedFor(country);
        // AB#1682 — fire BE creates for each new seed row. Replace the
        // temp ids with the persisted ones as they come back.
        if (onAdd) {
            const updates = await Promise.all(seedRows.map(async row => {
                try {
                    const persistedId = await onAdd(row);
                    return persistedId ? { tempId: row.id, newId: persistedId } : null;
                } catch { return null; }
            }));
            const idMap = new Map(updates.filter((u): u is NonNullable<typeof u> => !!u)
                .map(u => [u.tempId, u.newId]));
            if (idMap.size > 0) {
                onChange(merged.map(c => idMap.has(c.id) ? { ...c, id: idMap.get(c.id)! } : c));
            }
        }
    };
    const dismissSeeds = () => setDismissedFor(country);

    const add = async () => {
        const newRow = newClosure();
        // Optimistic append to form state.
        onChange([...value, newRow]);
        // AB#1682 — persist to BE; swap temp id for persisted id on success.
        if (onAdd) {
            try {
                const persistedId = await onAdd(newRow);
                if (persistedId) {
                    onChange([...value, { ...newRow, id: persistedId }]);
                }
            } catch { /* toast already fired by mutation hook */ }
        }
    };

    const remove = async (id: string) => {
        onChange(value.filter(c => c.id !== id));
        if (onRemove) {
            try { await onRemove(id); } catch { /* toast fires upstream */ }
        }
    };

    const update = (id: string, patch: Partial<HolidayClosureForm>) => {
        const next = value.map(c => {
            if (c.id !== id) return c;
            const merged = { ...c, ...patch };
            if (patch.from !== undefined && merged.to < merged.from) merged.to = merged.from;
            return merged;
        });
        onChange(next);
    };

    /** AB#1682 — debounced PUT on field change so BE reflects the user's
     *  edits without an explicit save click. 600ms feels fast but avoids
     *  a request per keystroke in the reason text field. */
    const debounceTimers = React.useRef<Record<string, ReturnType<typeof setTimeout>>>({});
    const updateAndDebounce = (id: string, patch: Partial<HolidayClosureForm>) => {
        update(id, patch);
        if (!onUpdate) return;
        if (debounceTimers.current[id]) clearTimeout(debounceTimers.current[id]);
        debounceTimers.current[id] = setTimeout(() => {
            const row = value.map(c => (c.id === id ? { ...c, ...patch } : c)).find(c => c.id === id);
            if (row) onUpdate(row).catch(() => { /* toast fires upstream */ });
        }, 600);
    };

    return (
        <div className={styles.holidayWrap}>
            {showSeedBanner && (
                <div className={styles.holidaySeedBanner}>
                    <span className={styles.holidaySeedIcon} aria-hidden="true">🎁</span>
                    <div className={styles.holidaySeedBody}>
                        <strong>
                            {interpolate(t('mb.details.holidays.seedTitle'), {
                                n: seeds.length,
                                country: language === 'ar'
                                    ? (COUNTRIES.find(c => c.code === country)?.nameAr ?? country)
                                    : (COUNTRY_NAME_EN[country] ?? country),
                                year: 2026,
                            })}
                        </strong>
                        <span className={styles.holidaySeedNote}>{t('mb.details.holidays.seedNote')}</span>
                    </div>
                    <span className={styles.holidaySeedActions}>
                        <button type="button" className={styles.holidaySeedLoad} onClick={loadSeeds}>{t('mb.details.holidays.loadAll')}</button>
                        <button type="button" className={styles.holidaySeedDismiss} onClick={dismissSeeds} aria-label={t('mb.common.dismiss')}>×</button>
                    </span>
                </div>
            )}

            {value.length === 0 ? (
                <div className={styles.holidayEmpty}>
                    <span aria-hidden="true">📅</span>
                    <span>{t('mb.details.holidays.empty')}</span>
                </div>
            ) : (
                <div className={styles.holidayList}>
                    {value.map(c => {
                        const isRange = c.to !== c.from;
                        return (
                            <div key={c.id} className={styles.holidayRow}>
                                <span className={styles.holidayDates}>
                                    {/* O3 (SO-20) — day-first DateField over the native picker. */}
                                    <DateField
                                        value={c.from}
                                        onChange={(iso) => updateAndDebounce(c.id, { from: iso })}
                                        label={t('mb.details.holidays.from')}
                                    />
                                    <span className={styles.hoursDash}>–</span>
                                    <DateField
                                        value={c.to}
                                        min={c.from}
                                        onChange={(iso) => updateAndDebounce(c.id, { to: iso })}
                                        label={t('mb.details.holidays.to')}
                                    />
                                    {isRange && <span className={styles.holidayBadge}>{t('mb.details.holidays.range')}</span>}
                                </span>
                                <input
                                    type="text"
                                    className={styles.holidayReason}
                                    placeholder={t('mb.details.holidays.reasonPlaceholder')}
                                    value={c.reason}
                                    onChange={(e) => updateAndDebounce(c.id, { reason: e.target.value })}
                                    aria-label={t('mb.details.holidays.reason')}
                                />
                                <button
                                    type="button"
                                    className={styles.holidayRemove}
                                    onClick={() => remove(c.id)}
                                    aria-label={t('mb.details.holidays.remove')}
                                    title={t('mb.common.remove')}
                                >
                                    ×
                                </button>
                            </div>
                        );
                    })}
                </div>
            )}

            <button type="button" className={styles.holidayAdd} onClick={add}>
                <span aria-hidden="true">+</span> {t('mb.details.holidays.add')}
            </button>
        </div>
    );
};

/* ── Get Social input rows (uncontrolled — RHF registers them via spread) ─ */

interface SocialInputProps extends React.InputHTMLAttributes<HTMLInputElement> {
    icon: React.ReactNode;
}

const SocialInput = React.forwardRef<HTMLInputElement, SocialInputProps>(
    function SocialInput({ icon, ...inputProps }, ref) {
        return (
            <div className={styles.socialRow}>
                <span className={styles.socialIcon} aria-hidden="true">{icon}</span>
                <input ref={ref} type="text" {...inputProps} />
            </div>
        );
    },
);

/* ── Other settings (controlled block) ──────────────────────────────────── */

interface OtherSettingsBlockProps {
    value: OtherSettingsForm;
    onChange: (next: OtherSettingsForm) => void;
}

const OtherSettingsBlock: React.FC<OtherSettingsBlockProps> = ({ value, onChange }) => {
    const { t } = useLanguage();
    const set = <K extends keyof OtherSettingsForm>(key: K, v: OtherSettingsForm[K]) =>
        onChange({ ...value, [key]: v });

    return (
        <>
            <div className={styles.formGrid}>
                <div className={styles.field}>
                    <label htmlFor="bd-salonType">{t('mb.details.other.salonType')}</label>
                    <select
                        id="bd-salonType"
                        value={value.salonType}
                        onChange={(e) => set('salonType', e.target.value as OtherSettingsForm['salonType'])}
                    >
                        <option value="both">{t('mb.details.other.salonType.both')}</option>
                        <option value="male">{t('mb.details.other.salonType.male')}</option>
                        <option value="female">{t('mb.details.other.salonType.female')}</option>
                    </select>
                </div>
                <div className={styles.field}>
                    {/* O3 (SO-21) — plain wording; the value is how many decimal
                        places prices are shown with (pricingDecimalPlaces). */}
                    <label htmlFor="bd-decimal">{t('mb.details.other.decimals')}</label>
                    <select
                        id="bd-decimal"
                        value={value.decimalDigits}
                        onChange={(e) => set('decimalDigits', Number(e.target.value) as OtherSettingsForm['decimalDigits'])}
                    >
                        <option value={0}>{t('mb.details.other.decimals.0')}</option>
                        <option value={2}>{t('mb.details.other.decimals.2')}</option>
                        <option value={3}>{t('mb.details.other.decimals.3')}</option>
                    </select>
                </div>
            </div>

            <div className={styles.toggleList}>
                <ToggleRow
                    title={t('mb.details.other.autoConfirm')}
                    description={t('mb.details.other.autoConfirmHelp')}
                    checked={value.autoConfirmBookings}
                    onChange={(b) => set('autoConfirmBookings', b)}
                />
                <ToggleRow
                    title={t('mb.details.other.sameDay')}
                    description={t('mb.details.other.sameDayHelp')}
                    checked={value.allowWalkIns}
                    onChange={(b) => set('allowWalkIns', b)}
                />
                <ToggleRow
                    title={t('mb.details.other.ratings')}
                    description={t('mb.details.other.ratingsHelp')}
                    checked={value.showRatingsPublicly}
                    onChange={(b) => set('showRatingsPublicly', b)}
                />
            </div>
        </>
    );
};

interface ToggleRowProps {
    title: string;
    description: string;
    checked: boolean;
    onChange: (next: boolean) => void;
    /** Marker that this control's state lives in localStorage until Phase B. */
    localOnly?: boolean;
}

const ToggleRow: React.FC<ToggleRowProps> = ({ title, description, checked, onChange, localOnly }) => {
    const { t } = useLanguage();
    return (
    <label className={styles.toggleRow}>
        <span className={styles.toggleText}>
            <span className={styles.toggleTitle}>
                {title}
                {localOnly && <span className={styles.fieldHint}> {t('mb.details.other.localOnly')}</span>}
            </span>
            <span className={styles.toggleDescription}>{description}</span>
        </span>
        <span className={`${styles.toggle} ${checked ? styles.toggleOn : ''}`}>
            <input
                type="checkbox"
                checked={checked}
                onChange={(e) => onChange(e.target.checked)}
                className={styles.toggleInput}
            />
            <span className={styles.toggleThumb} aria-hidden="true" />
        </span>
    </label>
    );
};

/* ── Inline brand-mark icons (monochrome, inherit currentColor) ─────────── */

const FacebookIcon: React.FC = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M22 12a10 10 0 1 0-11.6 9.9V14.9H7.9V12h2.5V9.8c0-2.5 1.5-3.9 3.8-3.9 1.1 0 2.2.2 2.2.2v2.5h-1.3c-1.2 0-1.6.8-1.6 1.6V12h2.8l-.4 2.9h-2.4v7A10 10 0 0 0 22 12Z"/></svg>
);
const TwitterIcon: React.FC = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M18.9 4H22l-7.6 8.7L23 20h-7l-5.5-7.2L4.4 20H1.3l8.1-9.3L1 4h7.2l5 6.6L18.9 4Zm-2.4 14.3h1.7L7.7 5.6H5.9l10.6 12.7Z"/></svg>
);
const InstagramIcon: React.FC = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><rect x="3" y="3" width="18" height="18" rx="5"/><circle cx="12" cy="12" r="4"/><circle cx="17.5" cy="6.5" r="0.8" fill="currentColor"/></svg>
);
const WhatsAppIcon: React.FC = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M20 12a8 8 0 0 1-12 6.9L4 20l1.2-3.7A8 8 0 1 1 20 12Zm-9.6-3.1c-.2-.4-.4-.4-.6-.4h-.5c-.2 0-.5.1-.7.4-.2.3-.9.9-.9 2.2 0 1.3 1 2.6 1.1 2.7.1.2 1.8 2.9 4.5 4 2.2.9 2.6.7 3.1.7s1.5-.6 1.7-1.2c.2-.6.2-1.1.2-1.2-.1-.1-.3-.2-.5-.3-.3-.1-1.5-.7-1.7-.8-.2-.1-.4-.1-.6.1-.2.3-.7.8-.8 1-.2.2-.3.2-.5.1-.2-.1-1-.4-1.9-1.2-.7-.6-1.2-1.4-1.3-1.6-.1-.2 0-.4.1-.5l.4-.4c.1-.1.2-.3.3-.4.1-.2 0-.3 0-.5l-.4-1.7Z"/></svg>
);
const TikTokIcon: React.FC = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M16.5 2v3.4a4.6 4.6 0 0 0 4 4v3.5a8 8 0 0 1-4-1.1v6.7a6.4 6.4 0 1 1-6.4-6.4c.4 0 .7 0 1 .1v3.5a3 3 0 1 0 2.1 2.8V2h3.3Z"/></svg>
);
const MapPinIcon: React.FC = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M12 21s7-6 7-11a7 7 0 0 0-14 0c0 5 7 11 7 11Z"/><circle cx="12" cy="10" r="2.6"/></svg>
);
